import React, { FC, useEffect, useState } from "react";
import { Form } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../store/store";
import {
  errorMessage,
  successMessage,
} from "../../../../utils/notificationUtils";
import dayjs from "dayjs";
import DischargeStarHealth from "../../../../components/patient/Discharge/StarHealth/DischargeStarHealth";
import { getTimelineDetails } from "../../../../models/patientSlice";
import { fetchStepperData } from "../../../../models/preAuthSlice";
import {
  getAllStarhealthDocumentsData,
  deleteUploadedDocument,
  downloadDocument,
  updateAllDocumentsData,
  submitApprovalStarhealth,
} from "../../../../apis/patientService";
import {
  getBasicDetails,
  getDeficiencyQuestionData,
} from "../../../../models/patientSlice";
import {
  submitDischargeDataInEdgeProStarHealth,
  submitDischargeDataStarHealth,
  intimateSubmitDischargeStarHealth,
  fetchStarHealthRoomTypeList,
} from "../../../../models/DischargeSlice";
import { dischargeServiceStarhealth } from "../../../../apis/SchemeService/STARHEALTH/starHealthService";
import DocumentDeficiencyModal from "../../../../components/patient/DocumentDeficiencyModal";
import FilePreviewModal from "../../../../components/patient/PreAuth-query/FilePreviewModal";
import PatientDetailsStageDocument from "../../../../components/modals/PatientDetailsStageDocument";
import ConfirmSubmissionModalStage from "../../../../components/modals/ConfirmSubmissionModalStage";
import { UploadDocument } from "../../../../models/EnhancementSlice";
import { message } from "antd";
import { IDocumentFields } from "../../../../interfaces/ICase";
import MultipleFilesPreviewModal from "../../../../components/patient/PreAuth-query/MultipleFilesPreviewModal";

// Define proper interfaces
interface PreviewFile {
  url: string;
  name: string;
  showUpload: boolean;
  documentType: string;
}

interface SelectedFile {
  file: File;
  documentType: string;
}

interface Document {
  id: number;
  documentUrl: string;
  documentType: string;
  documentName: string;
  documentLabelType: string;
  referenceType: string | null;
  deficiencyCheckStatus: any;
  remarks: string | null;
  type?: string;
  file?: any;
  documentId?: any;
  label?: string;
  labelId?: string | number;
  requestDocument?: boolean;
  responseDocument?: boolean;
}

type DocumentUrl = Document;

interface DischargeStarHealthContainerProps {
  activeKey: any;
  sseData?: any;
  sseUpdateCount?: any;
}

const DischargeStarHealthContainer: FC<DischargeStarHealthContainerProps> = ({
  activeKey,
  sseData,
  sseUpdateCount
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch<AppDispatch>();
  const { patients, preAuth, auth, discharge, enhancement } = useSelector(
    (state: RootState) => state
  );

  // Loading states
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isIntimating, setIsIntimating] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  // Data states
  const [roomTypes, setRoomTypes] = useState<any[]>([]);
  const [documentTypes, setDocumentTypes] = useState<any[]>([]);
  const [documentUrls, setDocumentUrls] = useState<DocumentUrl[]>([]);
  const [
    documentRequestResponseDocuments,
    setDocumentRequestResponseDocuments,
  ] = useState<any>({});
  const [caseDischargeData, setCaseDischargeData] = useState<any>(null);
  const [caseDischargeId, setCaseDischargeId] = useState<number | null>(null);
  const [isApproved, setIsApproved] = useState<boolean>(false);
  const [previewModalVisible, setPreviewModalVisible] =
    useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<PreviewFile | null>(null);
  const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
  const [previewDocument, setPreviewDocument] = useState<boolean>(false);
  const [clickedActiveTab, setClickedActiveTab] = useState<string>("");
  const [deficiencyModal, setDeficiencyModal] = useState<boolean>(false);
  const [previewDocumentUrl, setPreviewDocumentUrl] = useState<string>("");
  const [documentName, setDocumentName] = useState<string>("");
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [fetchingDeficiencyData, setFetchingDeficiencyData] =
    useState<boolean>(false);
  const [isApprovedInitiated, setIsApprovedInitiated] =
    useState<boolean>(false);
  const [isDownloadingDocument, setIsDownloadingDocument] =
    useState<boolean>(false);
  const [downloadingDocumentUrl, setDownloadingDocumentUrl] =
    useState<string>("");
  const [uploadFileList, setUploadFileList] = useState<any[]>([]);
  const [clickedActiveDocumentUrl, setClickedActiveDocumentUrl] = useState<any>();

  const isDischargeTabActive = () => {
    if (!activeKey || activeKey.length === 0) return false;

    const currentStageId =
      typeof activeKey === "string"
        ? parseInt(activeKey)
        : parseInt(activeKey?.[0]);

    const currentStage = preAuth?.stepperData?.visitStages?.find(
      (stage: any) => stage?.visitStageId === currentStageId
    );

    return currentStage?.visitStage?.toUpperCase() === "DISCHARGE";
  };

  // Fetch stepper and timeline data
  const fetchStepperForStatus = async () => {
    if (patients?.fetchBasicDetails?.casesResponse?.id) {
      const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
      const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
      dispatch(fetchStepperData({ caseId: caseId, visitId: visitId }));
    }
  };

  const getTimelineDetail = async () => {
    if (
      patients?.fetchBasicDetails?.casesResponse?.id &&
      patients?.fetchBasicDetails?.visitInfoResponse?.id
    ) {
      await dispatch(
        getTimelineDetails({
          caseId: patients?.fetchBasicDetails?.casesResponse?.id,
          visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
        })
      );
    }
  };

  // Fetch document types
  const fetchDischargeDocuments = async () => {
    try {
      const response = await getDocumentTypes();
      setDocumentTypes(response);
    } catch (error) {
      errorMessage("Failed to fetch document types");
    }
  };

  useEffect(() => {
    if(activeKey && activeKey?.length > 0 && isDischargeTabActive()) {
    getDischargeData();
    // fetchStepperForStatus();
    // getTimelineDetail();
    fetchDischargeDocuments();
    }
  }, [
    preAuth?.portalData,
    patients?.fetchBasicDetails?.casesResponse?.id,
    patients?.fetchBasicDetails?.visitInfoResponse?.id,
    activeKey,
  ]);

  const fetchDataForSse = async () => {
      if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
        // dispatch(
        //   getBasicDetails({
        //     id: patients?.fetchBasicDetails?.visitInfoResponse?.id,
        //   })
        // );
      }
      getDischargeData();
      fetchStepperForStatus();
      await getTimelineDetail();
      fetchDocuments();
    };
  
    useEffect(() => {
      if (sseData) {
        fetchDataForSse();
      }
    }, [sseData, sseUpdateCount]);

  // Process document types into request/response documents
  useEffect(() => {
    const requestResponseDocuments = {
      requestedDocuments: [] as DocumentUrl[],
      responseDocuments: [] as DocumentUrl[],
    };

    documentTypes?.forEach((document) => {
      if (document?.requestDocument) {
        requestResponseDocuments.requestedDocuments.push(document);
      } else if (document?.responseDocument) {
        requestResponseDocuments.responseDocuments.push(document);
      }
    });

    setDocumentRequestResponseDocuments(requestResponseDocuments);
  }, [documentTypes]);

  useEffect(() => {
    const listOfUploadedDocuments = documentTypes?.flatMap(
      (item: any) =>
        item?.documentList?.map((doc: any) => ({
          id: doc?.id,
          documentName: doc?.documentName,
          documentType: doc?.documentType,
          documentUrl: doc?.documentUrl,
          documentLabelType: doc?.documentLabelType,
          deficiencyCheckStatus: doc?.deficiencyCheckStatus,
        })) || []
    );

    setDocumentUrls(listOfUploadedDocuments);
  }, [documentTypes]);

  // Document handling functions
  const fetchDocuments = async () => {
    if (
      patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId &&
      patients?.fetchBasicDetails?.casesResponse?.id &&
      patients?.fetchBasicDetails?.visitInfoResponse?.id
    ) {
      try {
        setLoading(true);
        const AllDocumentList = await getAllStarhealthDocumentsData(
          patients?.fetchBasicDetails?.casesResponse?.id,
          patients?.fetchBasicDetails?.visitInfoResponse?.id,
          patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId
        );
        setDocumentTypes(AllDocumentList?.["Discharge"]);
      } catch (err) {
        errorMessage("Please, Try again after sometime");
      } finally {
        setLoading(false);
      }
    }
  };

  const [selectedFiles, setSelectedFiles] = useState<
    { file: File; documentType: string }[]
  >([]);
  const [previewFiles, setPreviewFiles] = useState<
    {
      url: string;
      name: string;
      showUpload: boolean;
      documentType: string;
      isMultiple: boolean;
    }[]
  >([]);

  useEffect(() => {
    if (!previewModalVisible) {
      setSelectedFiles([]);
    }
  }, [previewModalVisible]);

  const handleFileChange =
    (documentType: string, isMultiple: boolean) => (e: any) => {
      if (isMultiple) {
        const files = e?.fileList || [];
        setUploadFileList(files);
        if (files.length === 0) return;

        const selected = files?.map((f: any) => ({
          file: f.originFileObj,
          documentType,
        }));

        const previews = files?.map((f: any) => ({
          url: URL.createObjectURL(f.originFileObj),
          name: f.name,
          showUpload: true,
          documentType,
          isMultiple: isMultiple,
        }));

        setSelectedFiles(selected);
        setPreviewFiles(previews);
        setPreviewModalVisible(true);
      } else {
        if (Array.isArray(e)) {
          return e;
        }
        if (e?.file) {
          setSelectedFile({ file: e.file, documentType: documentType });
          setPreviewFile({
            url: URL.createObjectURL(e.file),
            name: e.file.name,
            showUpload: true,
            documentType: documentType,
          });
          setPreviewModalVisible(true);
        }
        return e?.target?.files;
      }
    };

  // const handleFileChange = (documentType: string) => (e: any) => {
  //   if (Array.isArray(e)) {
  //     return e;
  //   }
  //   if (e?.file) {
  //     setSelectedFile({ file: e.file, documentType });
  //     setPreviewFile({
  //       url: URL.createObjectURL(e.file),
  //       name: e.file.name,
  //       showUpload: true,
  //       documentType,
  //     });
  //     setPreviewModalVisible(true);
  //   }
  //   return e?.target?.files;
  // };

  const handleOpen = (docType: any, documentUrl: any) => {
    setPreviewDocument(true);
    setClickedActiveTab(docType);
    setClickedActiveDocumentUrl(documentUrl);
  };

  const handleCancelPreview = () => {
    setPreviewDocument(false);
    setPreviewDocumentUrl("");
    setDocumentName("");
  };

  const handleDownloadDocument = async (
    fileName: string,
    url: string | undefined
  ) => {
    if (!url) {
      errorMessage("Document URL is missing");
      return;
    }

    try {
      setDownloadingDocumentUrl(url);
      setIsDownloadingDocument(true);
      await downloadDocument(fileName, url);
      successMessage("Document Downloaded Successfully");
    } catch (err) {
      errorMessage("Please, Try again later");
    } finally {
      setDownloadingDocumentUrl("");
      setIsDownloadingDocument(false);
    }
  };

  const handleDeleteList = async (
    documentType: string,
    documentUrl: string,
    i: number
  ) => {
    const combinedUrls = [...documentUrls];
    const deleteDocument = combinedUrls?.find(
      (doc) => doc.documentUrl === documentUrl
    );
    const deleteForm = {
      documentUrl: deleteDocument?.documentUrl,
      status: false,
      id: deleteDocument?.id,
    };

    const newUpdatedDocumentList = combinedUrls?.filter(
      (doc) => doc.documentUrl !== documentUrl
    );
    setDocumentUrls(newUpdatedDocumentList);

    if (deleteDocument?.documentUrl) {
      try {
        await deleteUploadedDocument(deleteForm);
      } catch (error) {
        errorMessage("Failed to delete document");
      }
    }
  };

  const handleDeficiencyModalOpen = async (
    documentUrl: string,
    documentId: number,
    document: any
  ) => {
    try {
      if (documentId) {
        setFetchingDeficiencyData(true);
        await dispatch(getDeficiencyQuestionData(document?.id));
        setSelectedDocument(document);
        setFetchingDeficiencyData(false);
        setPreviewDocumentUrl(documentUrl);
      }
    } catch (err) {
      errorMessage("Please, Try again after sometime");
      setSelectedDocument(null);
      setFetchingDeficiencyData(false);
    }
    setPreviewDocumentUrl(documentUrl);
    setSelectedDocument(document);
  };

  const closeDeficiencyModal = () => {
    setDeficiencyModal(false);
    setDocumentName("");
  };

  useEffect(() => {
    dispatch(
      fetchStarHealthRoomTypeList(
        patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0]
      )
    );
  }, [activeKey]);

  useEffect(() => {
    if (discharge?.starHealthRoomTypeList) {
      setRoomTypes(discharge?.starHealthRoomTypeList);
    }
  }, [discharge?.starHealthRoomTypeList]);

  const validateRequiredDocuments = (
    mandatoryDocuments: any,
    documentUrls: any
  ) => {
    for (const doc of mandatoryDocuments) {
      if (doc.required) {
        const isUploaded = documentUrls?.some(
          (uploadedDoc: any) => uploadedDoc.documentLabelType === doc.label
        );
        if (!isUploaded) {
          return false;
        }
      }
    }
    return true;
  };

  // Form submission handlers
  const handleSubmit = async () => {
    try {
      // await form.validateFields();
      const values = await form.getFieldsValue(true);
      setIsSaving(true);
      const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
      const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
      const portalMetaDataId =
        patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0];

      const updatedValues = {
        ...values,
        dateOfAdmission: values?.dateOfAdmission
          ? dayjs(values?.dateOfAdmission, "DD-MM-YYYY HH:mm:ss").format(
              "DD-MM-YYYY HH:mm:ss"
            )
          : null,
        dateOfDischarge: values?.dateOfDischarge
          ? dayjs(values?.dateOfDischarge, "DD-MM-YYYY HH:mm:ss").format(
              "DD-MM-YYYY HH:mm:ss"
            )
          : null,
        surgeryDate: values?.surgeryDate
          ? dayjs(values?.surgeryDate, "DD-MM-YYYY HH:mm:ss").format(
              "DD-MM-YYYY HH:mm:ss"
            )
          : null,
        discharge:
          values?.radioButtonForDischargeAndDeath === "true" ? false : true,
        death:
          values?.radioButtonForDischargeAndDeath === "true" ? true : false,
        hospitalId: patients?.fetchBasicDetails?.casesResponse?.healthCareId,
        schemeId: patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
        documentUrls,
        treatingDoctorName: values?.treatingDoctorName?.split("~")?.[0],
        roomType: values.roomType && {
          id: values.roomType.id,
          name: values.roomType.name,
        },
      };

      setIsSaving(true);
      const res = await dispatch(
        submitDischargeDataInEdgeProStarHealth({
          caseId,
          visitId,
          portalMetaDataId,
          caseDischargeId,
          dischargeData: updatedValues,
        })
      );

      if (submitDischargeDataInEdgeProStarHealth.fulfilled.match(res)) {
        successMessage("Discharge details saved successfully");
        setConfirmationModal(false);
        await getDischargeData();
        await fetchStepperForStatus();
        await getTimelineDetail();
        await fetchDocuments();
        if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
          dispatch(
            getBasicDetails({
              id: patients?.fetchBasicDetails?.visitInfoResponse?.id,
            })
          );
        }
      } else if (submitDischargeDataInEdgeProStarHealth.rejected.match(res)) {
        errorMessage("Failed to save discharge details");
        setConfirmationModal(false);
      }
    } catch (error: any) {
      if (error?.errorFields) {
        errorMessage("Please fill all required fields");
      } else {
        errorMessage(error?.message || "Failed to submit discharge");
      }
    } finally {
      setIsSaving(false);
      setConfirmationModal(false);
    }
  };

  const handleIntimate = async () => {
    try {
      // await form.validateFields();
      const values = await form.getFieldsValue(true);
      if (
        validateRequiredDocuments(
          documentRequestResponseDocuments["requestedDocuments"],
          documentUrls
        )
      ) {
        const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
        const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
        const portalMetaDataId =
          patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0];

        if (!caseId || !visitId || !portalMetaDataId) {
          errorMessage("Required case information is missing");
          return;
        }

        const updatedValues = {
          ...values,
          dateOfAdmission: values?.dateOfAdmission
            ? dayjs(values?.dateOfAdmission).format("DD-MM-YYYY HH:mm:ss")
            : null,
          dateOfDischarge: values?.dateOfDischarge
            ? dayjs(values?.dateOfDischarge).format("DD-MM-YYYY HH:mm:ss")
            : null,
          hospitalId:
            patients?.fetchBasicDetails?.casesResponse?.healthCareId || 0,
          schemeId:
            patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId || 0,
          documentUrls,
          action: "intimate",
          treatingDoctorName: values?.treatingDoctorName?.split("~")?.[0],
          roomType: values.roomType && {
            id: values.roomType.id,
            name: values.roomType.name,
          },
        };

        setIsIntimating(true);
        const res = await dispatch(
          intimateSubmitDischargeStarHealth({
            caseId,
            visitId,
            portalMetaDataId,
            intimationData: updatedValues,
          })
        );

        setIsIntimating(false);
        if (intimateSubmitDischargeStarHealth.fulfilled.match(res)) {
          successMessage("Discharge Sent To QC successfully");
          setConfirmationModal(false);
          await getDischargeData();
          await fetchStepperForStatus();
          await getTimelineDetail();
          await fetchDocuments();
          if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
            dispatch(
              getBasicDetails({
                id: patients?.fetchBasicDetails?.visitInfoResponse?.id,
              })
            );
          }
        } else if (intimateSubmitDischargeStarHealth.rejected.match(res)) {
          errorMessage("Failed to intimate discharge");
          setIsIntimating(false);
          setConfirmationModal(false);
        }
      } else {
        errorMessage("Please, Upload all required documents");
        setConfirmationModal(false);
        setIsIntimating(false);
      }
    } catch (error: any) {
      if (error?.errorFields) {
        errorMessage("Please fill all required fields");
      } else {
        errorMessage(error?.message || "Failed to intimate discharge");
      }
    } finally {
      setIsIntimating(false);
      setConfirmationModal(false);
    }
  };

  const handleSubmitOnPortal = async () => {
    try {
      await form.validateFields();
      const values = await form.getFieldsValue(true);
      if (
        validateRequiredDocuments(
          documentRequestResponseDocuments["requestedDocuments"],
          documentUrls
        )
      ) {
        const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
        const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
        const portalMetaDataId =
          patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0];

        if (!caseId || !visitId || !portalMetaDataId) {
          errorMessage("Required case information is missing");
          return;
        }

        const updatedValues = {
          ...values,
          dateOfAdmission: values?.dateOfAdmission
            ? dayjs(values?.dateOfAdmission).format("DD-MM-YYYY HH:mm:ss")
            : null,
          dateOfDischarge: values?.dateOfDischarge
            ? dayjs(values?.dateOfDischarge).format("DD-MM-YYYY HH:mm:ss")
            : null,
          hospitalId:
            patients?.fetchBasicDetails?.casesResponse?.healthCareId || 0,
          schemeId:
            patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId || 0,
          documentUrls,
          treatingDoctorName: values?.treatingDoctorName?.split("~")?.[0],
          roomType: values.roomType && {
            id: values.roomType.id,
            name: values.roomType.name,
          },
          integratedWithPortal:
            patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal,
        };

        setIsSubmitting(true);
        const res = await dispatch(
          submitDischargeDataStarHealth({
            caseId,
            visitId,
            portalMetaDataId,
            caseDischargeId,
            dischargeData: updatedValues,
          })
        );

        if (submitDischargeDataStarHealth.fulfilled.match(res)) {
          successMessage("Discharge submitted successfully");
          setConfirmationModal(false);
          await getDischargeData();
          await fetchStepperForStatus();
          await getTimelineDetail();
          await fetchDocuments();
          if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
            dispatch(
              getBasicDetails({
                id: patients?.fetchBasicDetails?.visitInfoResponse?.id,
              })
            );
          }
        } else if (submitDischargeDataStarHealth.rejected.match(res)) {
          errorMessage("Failed to submit discharge");
        }
      } else {
        errorMessage("Please, Upload all required documents");
        setConfirmationModal(false);
      }
    } catch (error: any) {
      if (error?.errorFields) {
        errorMessage("Please fill all required fields");
      } else {
        errorMessage(error?.message || "Failed to submit discharge");
      }
    } finally {
      setIsSubmitting(false);
      setConfirmationModal(false);
    }
  };

  // Data fetching functions
  const getDischargeData = async () => {
    try {
      const res = await dischargeServiceStarhealth.getDischargeData(
        patients?.fetchBasicDetails?.casesResponse?.id,
        patients?.fetchBasicDetails?.visitInfoResponse?.id
      );

      if (res && typeof res === "object") {
        setCaseDischargeData(res);
        setCaseDischargeId(res?.id);
        setIsApproved(res?.approved);

        const updatedValues = {
          ...res,
          dateOfAdmission:
            res?.dateOfAdmission &&
            dayjs(res?.dateOfAdmission, "DD-MM-YYYY HH:mm:ss", true).isValid()
              ? dayjs(res?.dateOfAdmission, "DD-MM-YYYY HH:mm:ss")
              : null,
          dateOfDischarge:
            res?.dateOfDischarge &&
            dayjs(res?.dateOfDischarge, "DD-MM-YYYY HH:mm:ss", true).isValid()
              ? dayjs(res?.dateOfDischarge, "DD-MM-YYYY HH:mm:ss")
              : null,
          roomType: res?.roomType,
        };

        form.setFieldsValue(updatedValues);
      }
    } catch (error: any) {
      errorMessage("Please, Try again later");
    }
  };

  // Add getDocumentTypes function
  const getDocumentTypes = async () => {
    try {
      const response = await getAllStarhealthDocumentsData(
        patients?.fetchBasicDetails?.casesResponse?.id || 0,
        patients?.fetchBasicDetails?.visitInfoResponse?.id || 0,
        patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId || 0
      );
      return response?.["Discharge"] || [];
    } catch (error) {
      errorMessage("Failed to fetch document types");
      return [];
    }
  };

  const updateOrAddDocument = (
    uploadedDocumentUrl: any,
    documentRes: IDocumentFields
  ) => {
    const updatedDocuments = [...uploadedDocumentUrl];

    updatedDocuments.push({
      documentType: documentRes?.documentType,
      documentName: documentRes?.documentName,
      documentUrl: documentRes?.documentUrl,
      documentLabelType: documentRes?.documentLabelType,
      id: documentRes?.id,
      deficiencyCheckStatus: documentRes?.deficiencyCheckStatus,
    });

    // Return the updated array (ensures it's a flat array)
    return updatedDocuments;
  };

  // Fix document upload handling
  // const handleUpload = async () => {
  //   const CollectPostData = new FormData();
  //   if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
  //     CollectPostData.append(
  //       "hospitalId",
  //       patients?.fetchBasicDetails?.casesResponse?.healthCareId?.toString()
  //     );
  //   }
  //   if (patients?.fetchBasicDetails?.patientResponse?.id) {
  //     CollectPostData.append(
  //       "patientId",
  //       patients?.fetchBasicDetails?.patientResponse?.id?.toString()
  //     );
  //   }
  //   if (patients?.fetchBasicDetails?.casesResponse?.id) {
  //     CollectPostData.append(
  //       "caseId",
  //       patients?.fetchBasicDetails?.casesResponse?.id?.toString()
  //     );
  //   }
  //   if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
  //     CollectPostData.append(
  //       "visitId",
  //       patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString()
  //     );
  //   }
  //   if (previewFile?.documentType) {
  //     CollectPostData.append("documentType", previewFile.documentType);
  //   }

  //   if (selectedFile?.file) {
  //     CollectPostData.append("file", selectedFile?.file);
  //   }

  //   try {
  //     const documentRes = await dispatch(
  //       UploadDocument({
  //         formData: CollectPostData,
  //         accessToken: auth.accessToken || "",
  //       })
  //     ).unwrap();

  //     const updatedDocuments = updateOrAddDocument(documentUrls, documentRes);

  //     setDocumentUrls(updatedDocuments);
  //     setPreviewModalVisible(false);
  //     message.success(
  //       `${documentRes?.documentLabelType} uploaded successfully`
  //     );
  //   } catch (error) {
  //     console.error("Failed to upload document:", error);
  //   }
  // };

  const handleUpload = async () => {
    if (selectedFiles?.length > 0) {
      const CollectPostData = new FormData();
      if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
        CollectPostData.append(
          "hospitalId",
          patients?.fetchBasicDetails?.casesResponse?.healthCareId?.toString()
        );
      }
      if (patients?.fetchBasicDetails?.patientResponse?.id) {
        CollectPostData.append(
          "patientId",
          patients?.fetchBasicDetails?.patientResponse?.id?.toString()
        );
      }
      if (patients?.fetchBasicDetails?.casesResponse?.id) {
        CollectPostData.append(
          "caseId",
          patients?.fetchBasicDetails?.casesResponse?.id?.toString()
        );
      }
      if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
        CollectPostData.append(
          "visitId",
          patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString()
        );
      }
      CollectPostData.append("documentType", previewFiles?.[0].documentType);

      selectedFiles?.map((fileObj, index) =>
        CollectPostData.append(`file${index}`, fileObj.file)
      );

      // if (selectedFiles?.length > 0) {
      //   selectedFiles.forEach((fileObj, index) => {
      //     // Extract the actual File object from your structure
      //     const actualFile = fileObj.file; // This is the originFileObj (actual File)

      //     if (actualFile instanceof File) {
      //       CollectPostData.append("files", actualFile);
      //     }
      //   });
      // }

      try {
        const documentRes = await dispatch(
          UploadDocument({
            formData: CollectPostData,
            accessToken: auth.accessToken || "",
          })
        ).unwrap();

        // const updatedDocuments = updateOrAddDocument(documentUrls, documentRes);
        const updatedDocuments = [...documentUrls, ...documentRes];

        setDocumentUrls(updatedDocuments);
        setPreviewModalVisible(false);
        message.success(
          `${documentRes?.[0]?.documentLabelType} uploaded successfully`
        );
        await handleAttachDocuments(documentRes);
        setUploadFileList([]);
        setSelectedFiles([]);
        setPreviewFiles([]);
      } catch (error) {
        console.error("Failed to upload document:", error);
      }
    } else {
      const CollectPostData = new FormData();
      if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
        CollectPostData.append(
          "hospitalId",
          patients?.fetchBasicDetails?.casesResponse?.healthCareId?.toString()
        );
      }
      if (patients?.fetchBasicDetails?.patientResponse?.id) {
        CollectPostData.append(
          "patientId",
          patients?.fetchBasicDetails?.patientResponse?.id?.toString()
        );
      }
      if (patients?.fetchBasicDetails?.casesResponse?.id) {
        CollectPostData.append(
          "caseId",
          patients?.fetchBasicDetails?.casesResponse?.id?.toString()
        );
      }
      if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
        CollectPostData.append(
          "visitId",
          patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString()
        );
      }
      if (previewFile?.documentType)
        CollectPostData.append("documentType", previewFile.documentType);

      if (selectedFile?.file) {
        CollectPostData.append("file", selectedFile?.file);
      }

      try {
        const documentRes = await dispatch(
          UploadDocument({
            formData: CollectPostData,
            accessToken: auth.accessToken || "",
          })
        ).unwrap();

        // const updatedDocuments = updateOrAddDocument(documentUrls, documentRes);
        const updatedDocuments = [...documentUrls, ...documentRes];

        setDocumentUrls(updatedDocuments);
        setPreviewModalVisible(false);
        message.success(
          `${documentRes?.[0]?.documentLabelType} uploaded successfully`
        );
        await handleAttachDocuments(documentRes);
        setSelectedFile(null);
        setPreviewFile({
          url: "",
          name: "",
          showUpload: false,
          documentType: "",
        });
      } catch (error) {
        console.error("Failed to upload document:", error);
      }
    }
  };

  const handleApprove = async () => {
    try {
      await form.validateFields();
      const values = await form.getFieldsValue(true);
      if (
        validateRequiredDocuments(
          documentRequestResponseDocuments["requestedDocuments"],
          documentUrls
        )
      ) {
        const updatedValues = {
          ...values,
          dateOfAdmission: values?.dateOfAdmission
            ? dayjs(values?.dateOfAdmission, "DD-MM-YYYY HH:mm:ss").format(
                "DD-MM-YYYY HH:mm:ss"
              )
            : null,
          dateOfDischarge: values?.dateOfDischarge
            ? dayjs(values?.dateOfDischarge, "DD-MM-YYYY HH:mm:ss").format(
                "DD-MM-YYYY HH:mm:ss"
              )
            : null,
          surgeryDate: values?.surgeryDate
            ? dayjs(values?.surgeryDate, "DD-MM-YYYY HH:mm:ss").format(
                "DD-MM-YYYY HH:mm:ss"
              )
            : null,
          discharge:
            values?.radioButtonForDischargeAndDeath === "true" ? false : true,
          death:
            values?.radioButtonForDischargeAndDeath === "true" ? true : false,
          hospitalId: patients?.fetchBasicDetails?.casesResponse?.healthCareId,
          schemeId:
            patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
          documentUrls,
          treatingDoctorName: values?.treatingDoctorName?.split("~")?.[0],
          roomType: values.roomType && {
            id: values.roomType.id,
            name: values.roomType.name,
          },
        };
        setIsApprovedInitiated(true);
        const formData = {
          caseId: patients?.fetchBasicDetails?.casesResponse?.id,
          visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
          stage: "Discharge",
          approved: true,
          dischargeRequest: updatedValues,
          caseDischargeId,
        };
        const res = await submitApprovalStarhealth(formData);
        setIsApproved(true);
        setIsApprovedInitiated(false);
        message.success("Discharge Approved Successfully");
        setConfirmationModal(false);
        await getTimelineDetail();
        await fetchStepperForStatus();
        await getDischargeData();
        await fetchDocuments();
        if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
          dispatch(
            getBasicDetails({
              id: patients?.fetchBasicDetails?.visitInfoResponse?.id,
            })
          );
        }
      } else {
        errorMessage("Please, Upload all required documents");
        setConfirmationModal(false);
        setIsApprovedInitiated(false);
        setIsApproved(false);
      }
    } catch (err: any) {
      if (err?.errorFields) {
        errorMessage("Please, fill all details before submitting");
        setIsApprovedInitiated(false);
        setIsApproved(false);
      } else {
        errorMessage("Please, Try again later");
        setIsApprovedInitiated(false);
        setIsApproved(false);
      }
      setConfirmationModal(false);
    }
  };

  const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
  const [title, setTitle] = useState<string>("");
  const [confirmationState, setConfirmationState] = useState<string>("");

  const handleAttachDocuments = async (updatedDocumentUrls: any) => {
    const payloadSchema: {
      casePreAuthAttachments: any[];
      caseEnhancementsAttachments: any[];
      caseDischargeAttachments: any[];
      caseClaimAttachments: any[];
    } = {
      casePreAuthAttachments: [],
      caseEnhancementsAttachments: [],
      caseDischargeAttachments: [],
      caseClaimAttachments: [],
    };

    const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
    const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
    const schemeMetaDataId =
      patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId;

    payloadSchema["caseDischargeAttachments"] = updatedDocumentUrls;

    if (caseId && visitId && schemeMetaDataId) {
      try {
        const AllDocumentList = await updateAllDocumentsData(
          caseId,
          visitId,
          payloadSchema
        );
        message.success(`documents saved successfully`);
        fetchDocuments();
      } catch (error) {
        message.error(`Something went wrong`);
      }
    }
  };

  const handleSaveData = async () => {
    const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
    const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
    const schemeMetaDataId =
      patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId;
    const payloadSchema: {
      casePreAuthAttachments: any[];
      caseEnhancementsAttachments: any[];
      caseDischargeAttachments: any[];
      caseClaimAttachments: any[];
    } = {
      casePreAuthAttachments: [],
      caseEnhancementsAttachments: [],
      caseDischargeAttachments: [],
      caseClaimAttachments: [],
    };

    const updatedDocumentUrls: any = documentUrls?.filter((document: any) =>
      documentRequestResponseDocuments["responseDocuments"]?.some(
        (doc: any) => doc?.label === document?.documentLabelType
      )
    );

    payloadSchema["caseDischargeAttachments"] = updatedDocumentUrls;

    if (caseId && visitId && schemeMetaDataId) {
      try {
        const AllDocumentList = await updateAllDocumentsData(
          caseId,
          visitId,
          payloadSchema
        );
        message.success(`Response documents saved successfully`);
        fetchDocuments();
      } catch (error) {
        message.error(`Something went wrong`);
      }
    }
  };

  const handleSaveConfirmationModal = async (confirmationState: string) => {
    if (confirmationState === "intimate") {
      try {
        await form.validateFields();
      } catch (err: any) {
        if (err?.errorFields) {
          errorMessage("Please fill all required fields");
        } else {
          errorMessage(err?.message || "Failed to intimate discharge");
        }
        return;
      }
      if (
        !validateRequiredDocuments(
          documentRequestResponseDocuments["requestedDocuments"],
          documentUrls,
        )
      ) {
        errorMessage("Please, Upload all required documents");
        return;
      }
    }

    setConfirmationModal(true);
    setConfirmationState(confirmationState);

    if (confirmationState === "save") {
      setTitle("Do You want to save Discharge ?");
    } else if (confirmationState === "update") {
      setTitle("Do You want to update Discharge ?");
    } else if (confirmationState === "intimate") {
      setTitle("Do you want to send Discharge for QC ?");
    } else if (confirmationState === "submit") {
      setTitle("Do You want to submit Discharge ?");
    } else if (confirmationState === "approve") {
      setTitle("Do You want to approve Discharge ?");
    }
  };

  const handleSave = async () => {
    if (confirmationState === "save") {
      await handleSubmit();
    } else if (confirmationState === "update") {
      await handleSubmit();
    } else if (confirmationState === "intimate") {
      await handleIntimate();
    } else if (confirmationState === "submit") {
      await handleSubmitOnPortal();
    } else if (confirmationState === "approve") {
      await handleApprove();
    }

    setConfirmationModal(false);
  };

  return (
    <>
      <DischargeStarHealth
        form={form}
        isLoading={loading}
        roomTypes={roomTypes}
        documentTypes={documentTypes}
        documentUrls={documentUrls}
        documentRequestResponseDocuments={documentRequestResponseDocuments}
        handleFileChange={handleFileChange}
        handleOpen={handleOpen}
        isEditable={
          !caseDischargeData?.submitted
        }
        isApproved={isApproved}
        handleSaveData={handleSaveData}
        preAuth={preAuth}
        activeKey={activeKey}
        isSubmitting={isSubmitting}
        isIntimating={isIntimating}
        isSaving={isSaving}
        handleSaveConfirmationModal={handleSaveConfirmationModal}
        caseDischargeData={caseDischargeData}
        loading={loading}
        isApprovedInitiated={isApprovedInitiated}
        uploadFileList={uploadFileList}
      />

      {previewModalVisible && selectedFile?.file && !selectedFiles?.length && (
        <FilePreviewModal
          isVisible={previewModalVisible}
          onClose={() => setPreviewModalVisible(false)}
          fileUrl={previewFile?.url || ""}
          fileName={previewFile?.name || ""}
          handleUpload={handleUpload}
          showUploadButton={true}
          documentType={previewFile?.documentType}
          key={selectedFile?.file?.lastModified || "no-file"}
          isLoading={enhancement?.isLoading}
        />
      )}

      {previewDocument && (
        <PatientDetailsStageDocument
          handleCancel={handleCancelPreview}
          handleDeleteDocument={handleDeleteList}
          open={previewDocument}
          handleDownloadDocument={handleDownloadDocument}
          documents={documentUrls}
          handleDeficiencyModal={handleDeficiencyModalOpen}
          closeDeficiencyModal={closeDeficiencyModal}
          deficiencyModal={deficiencyModal}
          deficiencyQuestions={patients?.deficiencyCheckQuestions}
          setDocumentName={setDocumentName}
          selectedDocument={selectedDocument}
          setUploadedDocumentUrl={setDocumentUrls}
          activeKey={activeKey}
          clickedActiveTab={clickedActiveTab}
          isSubmittedStatus={caseDischargeData?.submitted}
          fetchingDeficiencyData={fetchingDeficiencyData}
          clickedActiveDocumentUrl={clickedActiveDocumentUrl}
        />
      )}

      {deficiencyModal && selectedDocument && (
        <DocumentDeficiencyModal
          key={previewDocumentUrl}
          deficiencyModal={deficiencyModal}
          closeDeficiencyModal={() => setDeficiencyModal(false)}
          documentUrl={previewDocumentUrl}
          documentName={documentName}
          handleDownloadDocument={handleDownloadDocument}
          deficiencyQuestions={patients?.deficiencyCheckQuestions}
          selectedDocument={selectedDocument}
          setUploadedDocumentUrl={setDocumentUrls}
        />
      )}

      <ConfirmSubmissionModalStage
        open={confirmationModal}
        title={title}
        handleCancel={() => setConfirmationModal(false)}
        handleSave={handleSave}
        isLoading={
          isSaving || isIntimating || isApprovedInitiated || isSubmitting
        }
      />

      {selectedFiles?.length > 0 && (
        <MultipleFilesPreviewModal
          isVisible={previewModalVisible}
          onClose={() => {
            setPreviewModalVisible(false);
            setSelectedFiles([]);
            setUploadFileList([]);
          }}
          files={previewFiles}
          handleUpload={handleUpload}
          showUploadButton={previewFiles?.some((f) => f.showUpload)}
          key={previewFiles?.map((f) => f.name).join(",") || "no-file"}
          isLoading={enhancement?.isLoading}
        />
      )}
    </>
  );
};

export default DischargeStarHealthContainer;
